import { useSearchParams } from "react-router-dom";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import InvitationTab from "@/pages/marketing/InvitationTab";
import CampaignTab from "@/pages/marketing/CampaignTab";
import ContactsTab from "@/pages/marketing/ContactsTab";
import { useMarketingSettings } from "@/hooks/sms/useMarketingSettings";

const TABS = ["invitation", "campaign", "contacts"] as const;
type MarketingTab = (typeof TABS)[number];

/**
 * /marketing: one page, three tabs (captain-approved round-4 design) —
 * "Invitation" (the Order Invite standing play), "Campaign" (promotions +
 * the send composer) and "Contacts" (the customer roster with text club
 * status, plus the invite month roll-up; the nav's "Contacts" item lands here). No PIN gate: this is a feature page, not credentials;
 * destructive actions inside it are each gated by their own AlertDialog.
 * The active tab syncs to ?tab= so the composer and batch views deep-link.
 */
const Marketing = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { data: marketingSettings } = useMarketingSettings();

  const rawTab = searchParams.get("tab");
  const tab: MarketingTab = TABS.includes(rawTab as MarketingTab) ? (rawTab as MarketingTab) : "invitation";

  const setTab = (next: string) => {
    const params = new URLSearchParams(searchParams);
    params.set("tab", next);
    setSearchParams(params, { replace: true });
  };

  return (
    <div className="pb-4">
      <header className="mb-6 sm:mb-8">
        <h1 className="text-2xl font-bold text-foreground sm:text-3xl">Marketing</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Grow your text club and send offers to the customers who joined.
        </p>
      </header>

      <Tabs value={tab} onValueChange={setTab} className="w-full">
        <TabsList className="mb-4 h-auto flex-wrap">
          <TabsTrigger value="invitation">Invitation</TabsTrigger>
          <TabsTrigger value="campaign">Campaign</TabsTrigger>
          <TabsTrigger value="contacts">Contacts</TabsTrigger>
        </TabsList>

        <TabsContent value="invitation">
          <InvitationTab />
        </TabsContent>

        <TabsContent value="campaign">
          {marketingSettings ? (
            <CampaignTab storeId={marketingSettings.store_id} />
          ) : (
            <p className="py-8 text-center text-sm text-muted-foreground">Loading…</p>
          )}
        </TabsContent>

        <TabsContent value="contacts">
          <ContactsTab />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Marketing;
